import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { TeamMember } from "@/api/queries";
import { ActivityBar } from "@/components/common/ActivityBar";
import { UserAvatar } from "@/components/common/UserAvatar";
import { VacationBadge } from "@/components/common/VacationBadge";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDuration, formatSince, formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const stateBadge = { working: "success", idle: "warning", offline: "neutral" } as const;
const stateDot = { working: "bg-success-solid", idle: "bg-warning-solid", offline: "bg-muted" } as const;

/** Live status of the people: current project, time today and this week, activity, last screenshot. */
export function TeamTable({ items, pending, lang }: { items: TeamMember[]; pending: boolean; lang: string }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] text-left">
        <thead className="bg-surface-muted text-xs text-muted">
          <tr>
            <th className="px-5 py-2.5 font-medium">{t("dashboard.columns.employee")}</th>
            <th className="px-5 py-2.5 font-medium">{t("dashboard.columns.status")}</th>
            <th className="px-5 py-2.5 font-medium">{t("dashboard.columns.project")}</th>
            <th className="px-5 py-2.5 text-right font-medium">{t("dashboard.columns.today")}</th>
            <th className="px-5 py-2.5 text-right font-medium">{t("dashboard.columns.week")}</th>
            <th className="w-52 px-5 py-2.5 font-medium">{t("dashboard.columns.activity")}</th>
            <th className="px-5 py-2.5 font-medium">{t("dashboard.columns.screenshot")}</th>
          </tr>
        </thead>
        <tbody>
          {pending
            ? [0, 1, 2].map((i) => (
                <tr key={i} className="border-t">
                  <td colSpan={7} className="px-5 py-4">
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ))
            : items.map((m) => <TeamRow key={m.user.id} member={m} lang={lang} />)}
        </tbody>
      </table>
    </div>
  );
}

function TeamRow({ member: m, lang }: { member: TeamMember; lang: string }) {
  const { t } = useTranslation();
  const timelineHref = `/activity/${m.user.id}`;
  return (
    <tr className="border-t whitespace-nowrap">
      <td className="px-5 py-3">
        <Link to={timelineHref} className="flex items-center gap-3 font-medium hover:underline">
          <span className="relative">
            <UserAvatar name={m.user.full_name} />
            <span
              className={cn(
                "absolute right-0 bottom-0 size-2.5 rounded-full ring-2 ring-surface",
                stateDot[m.state],
              )}
            />
          </span>
          {m.user.full_name}
        </Link>
      </td>
      <td className="px-5 py-3">
        <span className="flex flex-wrap gap-1">
          {(m.state !== "offline" || !m.user.vacation?.active) && (
            <Badge variant={stateBadge[m.state]}>{t(`state.${m.state}`)}</Badge>
          )}
          <VacationBadge vacation={m.user.vacation} />
        </span>
      </td>
      <td className="px-5 py-3">
        {m.current ? (
          <div className="flex flex-col">
            <span className="flex items-center gap-1.5 font-medium">
              <span className="size-2 rounded-full" style={{ backgroundColor: m.current.project.color }} />
              {m.current.project.name}
            </span>
            <span className="pl-3.5 text-xs text-muted">
              {m.current.task_title ? `${m.current.task_title} · ` : ""}
              {formatSince(m.current.since, lang)}
            </span>
          </div>
        ) : (
          <span className="text-muted">{t("dashboard.notTracking")}</span>
        )}
      </td>
      <td className="px-5 py-3 text-right tabular-nums">{formatDuration(m.today_seconds, lang)}</td>
      <td className="px-5 py-3 text-right text-muted tabular-nums">{formatDuration(m.week_seconds, lang)}</td>
      <td className="px-5 py-3">
        {m.today_seconds > 0 ? (
          <ActivityBar percent={m.today_activity_percent} />
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="px-5 py-3">
        {m.last_screenshot ? (
          <Link to={timelineHref} aria-label={t("dashboard.openTimeline")} className="block w-20">
            <img
              src={m.last_screenshot.thumb_url}
              alt={t("timeline.screenshotAlt", { time: formatTime(m.last_screenshot.taken_at, lang) })}
              className="aspect-[16/10] w-20 rounded-md border object-cover"
              loading="lazy"
            />
          </Link>
        ) : (
          <div className="aspect-[16/10] w-20 rounded-md bg-surface-muted" />
        )}
      </td>
    </tr>
  );
}
